import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from "react";
import mermaid from "mermaid";
import { useKeyboardHandler } from "../../hooks/useKeyboardHandler";
export const FullscreenDiagramModal = ({ isOpen, onClose, mermaidCode }) => {
    const [zoomLevel, setZoomLevel] = useState(1.5);
    const containerRef = useRef(null);
    const [renderState, setRenderState] = useState("loading");
    const [content, setContent] = useState("");
    const renderDiagram = useCallback(async () => {
        if (!mermaidCode)
            return;
        setRenderState("loading");
        try {
            mermaid.initialize({
                startOnLoad: false,
                theme: "default",
                securityLevel: "loose",
                maxTextSize: 90000,
                maxEdges: 500,
                flowchart: {
                    htmlLabels: false,
                    curve: "basis",
                    padding: 20,
                    nodeSpacing: 60,
                    rankSpacing: 60,
                    diagramPadding: 30,
                },
                themeVariables: {
                    fontFamily: "Arial, sans-serif",
                    fontSize: "14px",
                },
            });
            const diagramId = `fullscreen-mermaid-diagram-${Date.now()}`;
            const { svg } = await mermaid.render(diagramId, mermaidCode);
            setContent(svg);
            setRenderState("success");
        }
        catch (error) {
            console.error("Fullscreen Mermaid rendering error:", error);
            const lines = mermaidCode.split("\n").filter((line) => line.trim() && !line.includes("%%"));
            const simpleView = lines
                .slice(1)
                .map((line) => {
                if (line.includes("-->")) {
                    return `<div style="margin: 8px 0; padding: 8px; background: #f8f9fa; border-left: 3px solid #28a745; font-family: monospace;">${line.trim()}</div>`;
                }
                else if (line.includes("[") && line.includes("]")) {
                    return `<div style="margin: 8px 0; padding: 8px; background: #e7f3ff; border-left: 3px solid #374B94; font-weight: bold;">${line.trim()}</div>`;
                }
                return `<div style="margin: 4px 0; color: #666; font-family: monospace;">${line.trim()}</div>`;
            })
                .join("");
            const fallbackContent = `
        <div style="padding: 20px; border: 1px solid #e9ecef; background-color: #f8f9fa; border-radius: 6px;">
          <div style="text-align: center; margin-bottom: 16px; color: #856404; background: #fff3cd; padding: 8px; border-radius: 4px;">
            <strong>📊 Simplified Flow View</strong><br/>
            <small>Interactive diagram temporarily unavailable - showing text representation</small>
          </div>
          <div style="max-height: 400px; overflow-y: auto;">
            ${simpleView}
          </div>
        </div>
      `;
            setContent(fallbackContent);
            setRenderState("fallback");
        }
    }, [mermaidCode]);
    useEffect(() => {
        if (isOpen) {
            renderDiagram();
        }
    }, [isOpen, renderDiagram]);
    useEffect(() => {
        if (containerRef.current && content && renderState !== "loading") {
            while (containerRef.current.firstChild) {
                containerRef.current.removeChild(containerRef.current.firstChild);
            }
            const wrapper = document.createElement("div");
            wrapper.innerHTML = content;
            while (wrapper.firstChild) {
                containerRef.current.appendChild(wrapper.firstChild);
            }
        }
    }, [content, renderState]);
    useKeyboardHandler({
        isActive: isOpen,
        onEscape: onClose,
        onZoomIn: () => setZoomLevel((prev) => Math.min(prev + 0.1, 3.0)),
        onZoomOut: () => setZoomLevel((prev) => Math.max(prev - 0.1, 0.2)),
        onResetZoom: () => setZoomLevel(1.5),
        preventDefault: true,
    });
    if (!isOpen) {
        return null;
    }
    return (_jsxs("div", { style: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "#000",
            zIndex: 10000,
            display: "flex",
            flexDirection: "column",
        }, children: [_jsxs("div", { style: {
                    position: "absolute",
                    top: "20px",
                    right: "20px",
                    zIndex: 11000,
                    display: "flex",
                    gap: "10px",
                }, children: [_jsx("button", { onClick: () => setZoomLevel((prev) => Math.min(prev + 0.1, 3.0)), style: {
                            padding: "8px 12px",
                            background: "rgba(255, 255, 255, 0.1)",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            color: "#fff",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "14px",
                        }, title: "Zoom In (+)", children: "\uD83D\uDD0D+" }), _jsx("button", { onClick: () => setZoomLevel((prev) => Math.max(prev - 0.1, 0.2)), style: {
                            padding: "8px 12px",
                            background: "rgba(255, 255, 255, 0.1)",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            color: "#fff",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "14px",
                        }, title: "Zoom Out (-)", children: "\uD83D\uDD0D-" }), _jsx("button", { onClick: () => setZoomLevel(1.5), style: {
                            padding: "8px 12px",
                            background: "rgba(255, 255, 255, 0.1)",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            color: "#fff",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "14px",
                        }, title: "Reset Zoom (0)", children: "\u21BB" }), _jsx("button", { onClick: onClose, style: {
                            padding: "8px 12px",
                            background: "rgba(255, 255, 255, 0.1)",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            color: "#fff",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "14px",
                        }, children: "\u2715 Close" })] }), _jsx("div", { style: {
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "60px 20px 20px 20px",
                }, children: renderState === "loading" ? (_jsxs("div", { style: {
                        color: "#fff",
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }, children: [_jsx("div", { style: { marginBottom: "10px", fontSize: "18px" }, children: "\uD83D\uDD04 Generating fullscreen flow diagram..." }), _jsx("div", { style: { fontSize: "14px", opacity: 0.7 }, children: "Loading interactive diagram..." })] })) : (_jsx("div", { ref: containerRef, style: {
                        transform: `scale(${zoomLevel})`,
                        transformOrigin: "center",
                        minWidth: "1200px",
                        minHeight: "800px",
                        maxWidth: "90vw",
                        maxHeight: "80vh",
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        padding: "20px",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                    } })) }), _jsx("div", { style: {
                    position: "absolute",
                    bottom: "20px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "rgba(255, 255, 255, 0.7)",
                    fontSize: "14px",
                    textAlign: "center",
                }, children: "Use +/- keys to zoom \u2022 Press 0 to reset zoom \u2022 Press ESC to close" })] }));
};
//# sourceMappingURL=FullscreenDiagramModal.js.map